<template>
  <div class="heat-engine-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$η = W / Q₁$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 渲染区 -->
      <div ref="canvasContainer" class="simulation-stage dark-stage">
        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>气缸压力 (P)</label>
              <span class="val" :style="{ color: phaseColor }">{{ pressure.toFixed(1) }} <small>atm</small></span>
           </div>
           <div class="hud-item orange">
              <label>气体温度 (T)</label>
              <span class="val" :style="{ color: phaseColor }">{{ (temperature - 273).toFixed(0) }} <small>°C</small></span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row">
              <div class="s-item flex-1">
                 <label>转速: {{ rpm }}</label>
                 <el-slider v-model="rpm" :min="100" :max="1200" :step="50" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button :type="isRunning ? 'danger' : 'primary'" size="small" @click="isRunning = !isRunning" :icon="isRunning ? 'el-icon-video-pause' : 'el-icon-video-play'">
                    {{ isRunning ? '停机' : '启动' }}
                 </el-button>
              </div>
           </div>
        </div>

        <div class="cycle-track">
           <div v-for="(p, idx) in phases" :key="p" class="cycle-step" :class="{ active: currentPhaseIdx === idx }">
              <div class="dot"></div>
              <span class="name">{{ p }}</span>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>热力学原理：</strong>四个冲程完成一个工作循环。<strong>压缩冲程</strong>机械能转内能，<strong>做功冲程</strong>内能转机械能。每个循环曲轴旋转<strong>两周</strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'HeatEngineViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      rpm: 400,
      isRunning: true,
      time: 0,
      phases: ['吸气', '压缩', '做功', '排气'],
      running: true
    }
  },
  computed: {
    cycleProgress() { return (this.time % (Math.PI * 4)) / (Math.PI * 4); },
    currentPhaseIdx() { return Math.floor(this.cycleProgress * 4); },
    phaseColor() {
      const colors = ['#22d3ee', '#f59e0b', '#ef4444', '#94a3b8'];
      return colors[this.currentPhaseIdx];
    },
    pressure() {
       const progress = (this.time % (Math.PI * 1)) / Math.PI;
       if (this.currentPhaseIdx === 1) return 1 + progress * 25;
       if (this.currentPhaseIdx === 2) return 55 - progress * 40;
       return 1.2;
    },
    temperature() { return 293 + this.pressure * 60; }
  },
  mounted() { this.initThree(); },
  beforeDestroy() { 
    this.running = false;
    if (this.frameId) cancelAnimationFrame(this.frameId);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
    this.scene = null;
    this.camera = null;
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x020617);

      this.camera = new THREE.PerspectiveCamera(45, width / height, 1, 1000);
      this.camera.position.set(40, 50, 90);
      this.camera.lookAt(0, 15, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      container.appendChild(this.renderer.domElement);

      // cylinder
      const cylGeo = new THREE.CylinderGeometry(15, 15, 45, 32, 1, true);
      const cylMat = new THREE.MeshPhongMaterial({ color: 0x334155, transparent: true, opacity: 0.25, side: THREE.DoubleSide });
      this.scene.add(new THREE.Mesh(cylGeo, cylMat));
      
      // piston
      this.piston = new THREE.Mesh(new THREE.CylinderGeometry(14.8, 14.8, 12, 32), new THREE.MeshPhongMaterial({ color: 0x94a3b8, specular: 0xffffff }));
      this.scene.add(this.piston);

      // Gas
      this.gas = new THREE.Mesh(new THREE.CylinderGeometry(14.5, 14.5, 45, 32), new THREE.MeshPhongMaterial({ color: 0x06b6d4, transparent: true, opacity: 0.6 }));
      this.gas.position.y = 22.5;
      this.scene.add(this.gas);

      // Light
      this.spark = new THREE.PointLight(0xef4444, 0, 80);
      this.spark.position.set(0, 45, 0);
      this.scene.add(this.spark);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.4));
      const light = new THREE.DirectionalLight(0xffffff, 0.9);
      light.position.set(30, 80, 40);
      this.scene.add(light);

      this.animate();
    },
    animate() {
      if (this.frameId) cancelAnimationFrame(this.frameId);
      if (!this.running) return;
      this.frameId = requestAnimationFrame(this.animate);
      if (this.isRunning) this.time += (this.rpm / 4800);

      const angle = this.time;
      if (this.piston) {
        const pistonY = Math.cos(angle) * 16 + 16;
        this.piston.position.y = pistonY;

        // gas
        const gasHeight = 45 - pistonY;
        this.gas.scale.y = Math.max(0.1, gasHeight / 45);
        this.gas.position.y = 45 - (gasHeight / 2);
        this.gas.material.color.set(this.phaseColor);
        
        const phaseT = (this.time % (Math.PI * 4));
        this.spark.intensity = Math.abs(phaseT - Math.PI * 2) < 0.15 ? 4 : 0;
      }
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.heat-engine-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; z-index: 10; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f1f5f9; font-weight: 800; small { font-size: 10px; opacity: 0.6; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; .flex-row { display: flex; align-items: center; gap: 20px; } }
.s-item { label { font-size: 12px; color: #64748b; font-weight: 700; display: block; margin-bottom: 2px; } }
.btn-group { .el-button { font-weight: 700; width: 100px; } }
.cycle-track { display: flex; gap: 6px; .cycle-step { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 8px 4px; border-radius: 8px; background: #f8fafc; border: 1px solid #f1f5f9; position: relative; .dot { width: 6px; height: 6px; border-radius: 50%; background: #e2e8f0; } .name { font-size: 11px; color: #94a3b8; font-weight: 700; } &.active { background: #f0f9ff; border-color: #3b82f6; .dot { background: #3b82f6; box-shadow: 0 0 8px rgba(59,130,246,0.5); } .name { color: #2563eb; } } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
